import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Modal } from '@shopify/polaris';
import XForm from 'src/components/layout/xform/XForm';
import { Graph } from 'src/generated/graph';
import { useCustomToast } from 'src/contexts/ToastProvider';

const MUTATION_ADD_SUPPLIER_QUICK = gql`
  mutation AddSupplierQuick(
    $phoneNumber: String!
    $lat: String!
    $lng: String!
    $address: String!
    $contact: String
    $name: String
  ) {
    addSupplierQuick(phoneNumber: $phoneNumber, lat: $lat, lng: $lng, address: $address, contact: $contact, name: $name)
  }
`;

interface Props {
  onSuccess?: () => void;
}

export interface CreateSupplierDialogRef {
  open: () => void;
}

const CreateSupplierDialog = forwardRef<CreateSupplierDialogRef, Props>(({ onSuccess }, ref) => {
  const { onToast } = useCustomToast();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [contact, setContact] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [locationInput, setLocationInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isFreightSeller, setIsFreightSeller] = useState(false);

  const [addSupplier, mutationProps] = useMutation<Graph.Mutation>(MUTATION_ADD_SUPPLIER_QUICK, {
    refetchQueries: ['supplierList'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setShowCreateDialog(false);
      resetForm();
      onToast({ content: 'Seller created successfully!', status: 'success' });
      if (onSuccess) {
        onSuccess();
      }
    },
  });

  useImperativeHandle(ref, () => ({
    open: () => setShowCreateDialog(true),
  }));

  const resetForm = () => {
    setName('');
    setPhoneNumber('');
    setContact('');
    setAddress('');
    setLat(null);
    setLng(null);
    setLocationInput('');
    setErrors({});
    setIsFreightSeller(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    if (!address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!lat || !lng) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSaveClicked = (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    addSupplier({
      variables: {
        phoneNumber: phoneNumber.trim(),
        lat: lat?.toString(),
        lng: lng?.toString(),
        address: address.trim(),
        contact: contact.trim() || undefined,
        name: name.trim() || undefined,
        isFreightSeller,
      },
    });
  };

  const supplierCreateForm = (
    <div>
      <XForm onSubmit={onSaveClicked}>
        <XForm.Division>
          <XForm.Text
            label="Phone Number *"
            value={phoneNumber}
            placeholder="Enter phone number"
            onChange={e => {
              setPhoneNumber(e.currentTarget.value);
              if (errors.phoneNumber) setErrors({ ...errors, phoneNumber: '' });
            }}
            required
            error={errors.phoneNumber}
          />
          <XForm.Text
            label="Seller Name"
            value={name}
            placeholder="Enter seller name (optional)"
            onChange={e => setName(e.currentTarget.value)}
          />
        </XForm.Division>
        <XForm.Division>
          <XForm.Text
            label="Contact"
            value={contact}
            placeholder="Enter contact (optional)"
            onChange={e => setContact(e.currentTarget.value)}
          />
          <XForm.Text
            label="Location (Latitude, Longitude) *"
            type="text"
            value={locationInput}
            placeholder="Enter latitude, longitude (e.g., 11.541720554133311, 104.9179089747673)"
            onChange={e => {
              const value = e.currentTarget.value;
              setLocationInput(value);

              const parts = value.split(',').map(p => p.trim());

              if (parts.length === 2) {
                const latValue = parseFloat(parts[0]);
                const lngValue = parseFloat(parts[1]);

                if (!isNaN(latValue) && !isNaN(lngValue)) {
                  setLat(latValue);
                  setLng(lngValue);
                  if (errors.location) setErrors({ ...errors, location: '' });
                }
              } else if (value === '') {
                setLat(null);
                setLng(null);
              }
            }}
            required
            error={errors.location}
          />
        </XForm.Division>

        <XForm.Division>
          <div>
            <XForm.TextArea
              label="Address *"
              value={address}
              placeholder="Enter address"
              onChange={e => {
                setAddress(e.currentTarget.value);
                if (errors.address) setErrors({ ...errors, address: '' });
              }}
              required
              rows={3}
            />
            {errors.address && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.address}</div>}
          </div>
        </XForm.Division>
      </XForm>
      <XForm.Division>
        <XForm.Check
          label="Freight Seller"
          checked={isFreightSeller}
          onChange={e => setIsFreightSeller(e.currentTarget.checked)}
        />
      </XForm.Division>
    </div>
  );

  return (
    <Modal
      open={showCreateDialog}
      large
      instant
      onClose={() => undefined}
      title="Create New Seller"
      primaryAction={{
        content: 'Create',
        onAction: () => {
          onSaveClicked();
        },
        loading: mutationProps.loading,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: () => {
            setShowCreateDialog(false);
            resetForm();
          },
        },
      ]}
    >
      <style>{`
        .Polaris-Modal-CloseButton {
          display: none !important;
        }
      `}</style>
      <Modal.Section>{supplierCreateForm}</Modal.Section>
    </Modal>
  );
});

CreateSupplierDialog.displayName = 'CreateSupplierDialog';

export default CreateSupplierDialog;
